import React, { useState } from "react";
import { toast } from "sonner";
import { Gift, MapPin, Minus, Plus, Star } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { SITE } from "@/data/site";
import { czk } from "@/lib/price";

const inputCls = "bg-brand-bg border-brand-border text-white h-9 text-sm";
const loc = (k) => SITE.locations.find((l) => l.key === k)?.name.replace("Praha ", "") || k;
const KIND = { manual: "Ruční úprava", visit: "Návštěva", reward: "Odměna" };
const fmt = (iso) => new Date(iso).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });

function Block({ title, children, testId }) {
  return (
    <div className="bg-brand-bg border border-brand-border p-4 space-y-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">{title}</div>
      {children}
    </div>
  );
}

function PointsForm({ onApply, busy }) {
  const [amount, setAmount] = useState("10");
  const [reason, setReason] = useState("");
  const apply = (sign) => onApply(sign * Math.abs(Number(amount) || 0), reason).then(() => setReason(""));
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Input type="number" min={1} data-testid="points-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputCls} w-24`} />
      <Input data-testid="points-reason" placeholder="Důvod (např. kompletní detailing)" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputCls} flex-1 min-w-[180px]`} />
      <button disabled={busy || !Number(amount)} onClick={() => apply(1)} data-testid="points-add" className="h-9 px-3 bg-brand-accent text-black text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"><Plus size={13} /> Přidat</button>
      <button disabled={busy || !Number(amount)} onClick={() => apply(-1)} data-testid="points-remove" className="h-9 px-3 border border-brand-border text-xs inline-flex items-center gap-1 hover:border-red-400 hover:text-red-400 disabled:opacity-50"><Minus size={13} /> Odebrat</button>
    </div>
  );
}

function VisitForm({ member, onVisit, busy }) {
  const [f, setF] = useState({ location: member.preferred_location || "cestlice", paid_amount: "", points: "10", note: "" });
  return (
    <div className="grid sm:grid-cols-5 gap-2">
      <select data-testid="visit-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="h-9 bg-brand-bg border border-brand-border text-white text-sm px-2">
        {SITE.locations.map((l) => <option key={l.key} value={l.key}>{l.name.replace("Praha ", "")}</option>)}
      </select>
      <Input type="number" min={0} data-testid="visit-paid" placeholder="Uhrazeno Kč" value={f.paid_amount} onChange={(e) => setF({ ...f, paid_amount: e.target.value })} className={inputCls} />
      <Input type="number" min={0} data-testid="visit-points" placeholder="Body" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} className={inputCls} />
      <Input data-testid="visit-note" placeholder="Poznámka (služba)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inputCls} />
      <button disabled={busy} onClick={() => onVisit({ ...f, paid_amount: Number(f.paid_amount) || 0, points: Number(f.points) || 0 }).then(() => setF({ ...f, paid_amount: "", note: "" }))} data-testid="visit-submit"
        className="h-9 px-3 border border-brand-accent text-brand-accent text-xs font-semibold hover:bg-brand-accent hover:text-black disabled:opacity-50 inline-flex items-center justify-center gap-1"><MapPin size={13} /> Zapsat návštěvu</button>
    </div>
  );
}

/** Admin side panel for one member: points, rewards, visits, offers, history. `data` = response of /admin/club/scan or /profile. */
export function MemberProfile({ data, onChange, onClose }) {
  const [busy, setBusy] = useState(false);
  const [offerId, setOfferId] = useState("");
  if (!data) return null;
  const { member, points, point_transactions, rewards, visits, redemptions, offers } = data;

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); onChange(r.data); toast.success(typeof ok === "function" ? ok(r.data) : ok); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); throw e; }
    finally { setBusy(false); }
  };
  const changePoints = (delta, reason) => run(() => adminApi.post(`/admin/club/members/${member.id}/points`, { delta, reason }), (d) => `Body upraveny (${delta > 0 ? "+" : ""}${delta}). Stav: ${d.points}.`).catch(() => {});
  const visit = (body) => run(() => adminApi.post(`/admin/club/members/${member.id}/visit`, body), "Návštěva zapsána.").catch(() => {});
  const redeemReward = (r) => window.confirm(`Uplatnit odměnu „${r.title}“ za ${r.cost_points} bodů?`) && run(() => adminApi.post(`/admin/club/rewards/${r.id}/redeem`, { member_id: member.id }), (d) => `Odměna uplatněna. Zbývá ${d.points} bodů.`).catch(() => {});
  const redeemOffer = () => run(async () => { await adminApi.post("/admin/club/redemptions", { member_id: member.id, offer_id: offerId }); setOfferId(""); return adminApi.get(`/admin/club/members/${member.id}/profile`); }, "Čerpání výhody zaznamenáno.").catch(() => {});

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-brand-surface border-brand-border text-white overflow-y-auto" data-testid="member-profile">
        <SheetHeader className="text-left">
          <SheetTitle className="text-white font-display font-black text-2xl" data-testid="profile-name">{member.name}</SheetTitle>
          <SheetDescription className="text-brand-muted text-xs">
            <span className="font-mono text-brand-accent" data-testid="profile-number">{member.member_number}</span> · {loc(member.preferred_location)} · člen od {new Date(member.created_at).toLocaleDateString("cs-CZ")}
            <br />{member.email}{member.phone && ` · ${member.phone}`}{member.vehicle?.make_model && ` · ${member.vehicle.make_model}`}{member.vehicle?.plate && ` (${member.vehicle.plate})`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[["Body", points, "profile-points", true], ["Návštěv", visits.length, "profile-visits-count"], ["Výhod čerpáno", redemptions.length, "profile-redemptions-count"]].map(([l, v, id, acc]) => (
              <div key={id} className="bg-brand-bg border border-brand-border p-3" data-testid={id}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{l}</div>
                <div className={`font-display font-black text-2xl ${acc ? "text-brand-accent" : ""}`}>{v}</div>
              </div>
            ))}
          </div>

          <Block title="Body — přidat / odebrat" testId="profile-points-block"><PointsForm onApply={changePoints} busy={busy} /></Block>

          <Block title="Zapsat návštěvu (dokončená, uhrazená)" testId="profile-visit-block"><VisitForm member={member} onVisit={visit} busy={busy} /></Block>

          <Block title="Odměny za body" testId="profile-rewards-block">
            {rewards.length === 0 ? <div className="text-xs text-brand-muted">Katalog odměn je prázdný — přidejte odměny v sekci „Odměny za body“.</div> : rewards.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`profile-reward-${r.id}`}>
                <div className="min-w-0"><div className="font-semibold flex items-center gap-2"><Gift size={13} className="text-brand-accent shrink-0" />{r.title}</div>{r.description && <div className="text-xs text-brand-muted">{r.description}</div>}</div>
                <button disabled={busy || points < r.cost_points} onClick={() => redeemReward(r)} data-testid={`reward-redeem-${r.id}`}
                  className="h-8 px-3 text-xs font-semibold border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-brand-accent whitespace-nowrap">
                  {r.cost_points} b. · uplatnit
                </button>
              </div>
            ))}
          </Block>

          <Block title="Členská výhoda (bez bodů)" testId="profile-offer-block">
            <div className="flex gap-2">
              <select data-testid="profile-offer-select" value={offerId} onChange={(e) => setOfferId(e.target.value)} className="h-9 flex-1 bg-brand-bg border border-brand-border text-white text-sm px-2">
                <option value="">Vyberte výhodu…</option>
                {offers.map((o) => <option key={o.id} value={o.id}>{o.title}{o.active ? "" : " (skrytá)"}</option>)}
              </select>
              <button disabled={busy || !offerId} onClick={redeemOffer} data-testid="profile-offer-redeem" className="h-9 px-3 text-xs font-semibold bg-brand-accent text-black disabled:opacity-50">Zaznamenat čerpání</button>
            </div>
          </Block>

          <Block title="Historie bodů" testId="profile-history">
            {point_transactions.length === 0 ? <div className="text-xs text-brand-muted">Zatím žádný pohyb bodů.</div> : (
              <div className="max-h-56 overflow-auto text-sm divide-y divide-brand-border/60">
                {point_transactions.map((t) => (
                  <div key={t.id} className="py-1.5 flex justify-between gap-3" data-testid={`tx-${t.id}`}>
                    <span className="min-w-0 truncate"><span className={`font-mono font-bold ${t.delta > 0 ? "text-brand-accent" : "text-red-400"}`}>{t.delta > 0 ? "+" : ""}{t.delta}</span> · {KIND[t.kind] || t.kind}{t.reason && <span className="text-brand-muted"> · {t.reason}</span>}</span>
                    <span className="text-brand-muted text-xs whitespace-nowrap">{fmt(t.at)} · {t.by}</span>
                  </div>
                ))}
              </div>
            )}
          </Block>

          <Block title="Návštěvy a čerpání výhod" testId="profile-visits">
            {visits.length === 0 && redemptions.length === 0 ? <div className="text-xs text-brand-muted">Zatím žádná dokončená návštěva.</div> : (
              <div className="max-h-48 overflow-auto text-sm divide-y divide-brand-border/60">
                {visits.map((v) => <div key={v.id} className="py-1.5 flex justify-between gap-3"><span><Star size={11} className="inline text-brand-accent mr-1" />{loc(v.location)}{v.program_title && ` · ${v.program_title}`}{v.note && <span className="text-brand-muted"> · {v.note}</span>}</span><span className="text-brand-muted text-xs whitespace-nowrap">{czk(v.paid_amount ?? v.total_price)} · {new Date(v.completed_at || v.created_at).toLocaleDateString("cs-CZ")}</span></div>)}
                {redemptions.map((r) => <div key={r.id} className="py-1.5 flex justify-between gap-3"><span>Výhoda: {r.offer_title}{r.note && <span className="text-brand-muted"> · {r.note}</span>}</span><span className="text-brand-muted text-xs whitespace-nowrap">{new Date(r.at).toLocaleDateString("cs-CZ")}</span></div>)}
              </div>
            )}
          </Block>
        </div>
      </SheetContent>
    </Sheet>
  );
}
